import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataLabel } from "@/lib/utils-ui";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import ExportMenu from "@/components/ExportMenu";
import ConfirmDialog from "@/components/ConfirmDialog";

export default function CompaniesPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [form, setForm] = useState({ name: "", code: "", parent_id: "", address: "", email: "", phone: "" });

  const load = async () => { const { data } = await api.get("/companies"); setItems(data); };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      const payload = { ...form };
      if (!payload.parent_id) delete payload.parent_id;
      if (editing) {
        await api.put(`/companies/${editing.id}`, payload);
        toast.success("Company updated");
      } else {
        await api.post("/companies", payload);
        toast.success("Company created");
      }
      setOpen(false); setEditing(null); load();
      setForm({ name: "", code: "", parent_id: "", address: "", email: "", phone: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const startEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name, code: c.code, parent_id: c.parent_id || "",
      address: c.address || "", email: c.email || "", phone: c.phone || "",
    });
    setOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/companies/${deleting.id}`);
      toast.success("Company deleted");
      setDeleting(null); load();
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="p-8 space-y-6" data-testid="companies-page">
      <div className="flex items-start justify-between">
        <div>
          <DataLabel>Global Admin</DataLabel>
          <h1 className="font-head font-black uppercase text-4xl tracking-tighter mt-1">Companies</h1>
          <p className="text-sm text-muted-foreground mt-1">Parent company and subsidiaries. {items.length} tenants.</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportMenu module="companies" />
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
            <DialogTrigger asChild><Button data-testid="new-company-btn" onClick={() => { setEditing(null); setForm({ name: "", code: "", parent_id: "", address: "", email: "", phone: "" }); }} className="bg-black text-white hover:bg-[#FF5000] h-10 uppercase tracking-wider text-xs font-head"><Plus size={14} className="mr-1"/>New Company</Button></DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="font-head uppercase tracking-tight">{editing ? "Edit Company" : "Create Company"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="data-label">Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})}/></div>
              <div><Label className="data-label">Code</Label><Input className="font-mono" value={form.code} onChange={e => setForm({...form, code: e.target.value.toUpperCase()})}/></div>
              <div className="col-span-2"><Label className="data-label">Parent (optional)</Label>
                <Select value={form.parent_id || "_none"} onValueChange={(v) => setForm({...form, parent_id: v === "_none" ? "" : v})}>
                  <SelectTrigger><SelectValue placeholder="No parent"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">No parent</SelectItem>
                    {items.filter(c => !c.parent_id).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label className="data-label">Address</Label><Input value={form.address} onChange={e => setForm({...form, address: e.target.value})}/></div>
              <div><Label className="data-label">Email</Label><Input value={form.email} onChange={e => setForm({...form, email: e.target.value})}/></div>
              <div><Label className="data-label">Phone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}/></div>
            </div>
            <DialogFooter><Button onClick={create} className="bg-black text-white hover:bg-[#FF5000]">{editing ? "Save" : "Create"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <ConfirmDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}
        title="Delete company?" description={`${deleting?.name}. All its data remains intact.`} confirmLabel="Delete" variant="destructive"
        onConfirm={confirmDelete} testid="delete-company" />

      <div className="border border-border bg-white overflow-x-auto" data-testid="companies-table">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border bg-muted">
            {["NAME","CODE","PARENT","EMAIL","PHONE","ACTIONS"].map(h => <th key={h} className="text-left p-3 data-label">{h}</th>)}
          </tr></thead>
          <tbody>
            {items.map(c => {
              const parent = items.find(p => p.id === c.parent_id);
              return (
                <tr key={c.id} className="border-b border-border hover-row" data-testid={`company-row-${c.id}`}>
                  <td className="p-3 font-semibold">{c.name}</td>
                  <td className="p-3 font-mono text-xs">{c.code}</td>
                  <td className="p-3 text-xs">{parent?.name || "— (root)"}</td>
                  <td className="p-3 text-xs">{c.email || "—"}</td>
                  <td className="p-3 text-xs">{c.phone || "—"}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <button data-testid={`edit-company-${c.id}`} onClick={() => startEdit(c)} title="Edit" className="hover:text-[#FF5000]"><Pencil size={14}/></button>
                      <button data-testid={`delete-company-${c.id}`} onClick={() => setDeleting(c)} title="Delete" className="hover:text-[#FF3B30]"><Trash2 size={14}/></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

